import { useState } from 'react';

interface Props {
  balance: number;
  onClose: () => void;
}

type Method = 'wechat' | 'alipay' | 'bank';

const methods: { key: Method; label: string; icon: string; color: string; bg: string; placeholder: string }[] = [
  { key: 'wechat', label: '微信', icon: 'ri-wechat-pay-fill', color: 'text-green-600', bg: 'bg-green-50 border-green-300', placeholder: '请输入微信绑定手机号' },
  { key: 'alipay', label: '支付宝', icon: 'ri-alipay-fill', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-300', placeholder: '请输入支付宝账号（手机号/邮箱）' },
  { key: 'bank', label: '银行卡', icon: 'ri-bank-card-fill', color: 'text-gray-700', bg: 'bg-gray-50 border-gray-300', placeholder: '请输入银行卡号' },
];

export default function WithdrawModal({ balance, onClose }: Props) {
  const [method, setMethod] = useState<Method>('wechat');
  const [account, setAccount] = useState('');
  const [bankName, setBankName] = useState('');
  const [holderName, setHolderName] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const selectedMethod = methods.find(m => m.key === method)!;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const amt = Number(amount);
    if (!amount || amt <= 0) { setError('请输入提现金额'); return; }
    if (amt < 100) { setError('最低提现金额为 ¥100'); return; }
    if (amt > balance) { setError('提现金额不能超过可用余额'); return; }
    if (!account) { setError('请填写收款账号'); return; }
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 1800));
    setSubmitting(false);
    setDone(true);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-black text-gray-900 text-lg">申请提现</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 cursor-pointer transition-colors">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {done ? (
          <div className="p-10 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
              <i className="ri-checkbox-circle-fill text-5xl text-green-500"></i>
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">提现申请已提交！</h3>
            <p className="text-gray-500 text-sm mb-1">¥{amount} 将在 1-3 个工作日内到账</p>
            <p className="text-gray-400 text-xs mb-8">到账账号：{selectedMethod.label} {account}</p>
            <button onClick={onClose} className="px-8 py-3 bg-amber-500 text-white rounded-full font-bold hover:bg-amber-400 transition-colors cursor-pointer">
              完成
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
            {/* Balance Display */}
            <div className="bg-slate-800 rounded-2xl p-5 text-center">
              <p className="text-white/50 text-xs mb-1">可提现余额</p>
              <p className="text-amber-400 text-4xl font-black">¥{balance.toFixed(2)}</p>
              <p className="text-white/40 text-xs mt-1">最低提现 ¥100 · 每月最多5次</p>
            </div>

            {/* Method Selection */}
            <div>
              <label className="text-xs font-bold text-gray-600 mb-2 block">选择提现方式</label>
              <div className="grid grid-cols-3 gap-2">
                {methods.map(m => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => { setMethod(m.key); setAccount(''); }}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                      method === m.key ? m.bg : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${m.icon} text-2xl ${method === m.key ? m.color : 'text-gray-400'}`}></i>
                    <span className={`text-xs font-bold ${method === m.key ? m.color : 'text-gray-500'}`}>{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Account Input */}
            <div>
              <label className="text-xs font-bold text-gray-600 mb-1.5 block">收款账号</label>
              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-amber-400 transition-colors">
                <i className={`${selectedMethod.icon} ${selectedMethod.color} text-lg`}></i>
                <input
                  type="text"
                  value={account}
                  onChange={e => setAccount(e.target.value)}
                  placeholder={selectedMethod.placeholder}
                  className="flex-1 bg-transparent text-sm outline-none"
                />
              </div>
            </div>

            {/* Bank extra fields */}
            {method === 'bank' && (
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-600 mb-1.5 block">开户银行</label>
                  <input
                    type="text"
                    value={bankName}
                    onChange={e => setBankName(e.target.value)}
                    placeholder="如：中国工商银行"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 mb-1.5 block">持卡人姓名</label>
                  <input
                    type="text"
                    value={holderName}
                    onChange={e => setHolderName(e.target.value)}
                    placeholder="请输入持卡人真实姓名"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Amount */}
            <div>
              <label className="text-xs font-bold text-gray-600 mb-1.5 block">提现金额（元）</label>
              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-amber-400 transition-colors">
                <span className="text-gray-500 font-bold">¥</span>
                <input
                  type="number"
                  min="100"
                  max={balance}
                  step="1"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="最低 100"
                  className="flex-1 bg-transparent text-sm outline-none font-bold"
                />
                <button
                  type="button"
                  onClick={() => setAmount(Math.floor(balance).toString())}
                  className="text-xs text-amber-600 font-bold hover:underline cursor-pointer whitespace-nowrap"
                >
                  全部提现
                </button>
              </div>
              {amount && Number(amount) > 0 && (
                <p className="text-xs text-gray-400 mt-1.5">
                  预计到账：<span className="font-bold text-gray-700">¥{Number(amount).toFixed(2)}</span>（无手续费）
                </p>
              )}
            </div>

            {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 bg-amber-500 text-white rounded-full font-black text-base hover:bg-amber-400 transition-colors cursor-pointer disabled:opacity-60"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>处理中...
                </span>
              ) : `确认提现 ¥${amount || '0'}`}
            </button>

            <p className="text-center text-xs text-gray-400">
              <i className="ri-shield-check-line mr-1"></i>
              提现安全加密，1-3个工作日到账
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
